import { useEffect, useState } from "react";
import TopNav from "../components/TopNav";
import api from "../lib/api";
import { Target, Calendar, Copy, Loader2, CalendarPlus } from "lucide-react";

const STATUS_TAG = {
  scheduled: "tag-cyan",
  in_progress: "tag-amber",
  complete: "tag-green",
};

export default function Exercises() {
  const [exercises, setExercises] = useState([]);
  const [calOpen, setCalOpen] = useState(false);
  const [cal, setCal] = useState(null);
  const [calBusy, setCalBusy] = useState(false);
  const [copied, setCopied] = useState("");

  useEffect(() => { api.get("/exercises").then((r) => setExercises(r.data)); }, []);

  const openCalendar = async () => {
    setCalOpen(true);
    if (cal) return;
    setCalBusy(true);
    try {
      const { data } = await api.get("/me/calendar/subscription");
      setCal(data);
    } finally { setCalBusy(false); }
  };

  const copy = async (text, which) => {
    try { await navigator.clipboard.writeText(text); setCopied(which); setTimeout(() => setCopied(""), 1500); }
    catch (e) { console.error(e); }
  };

  const grouped = ["scheduled", "in_progress", "complete"].map((s) => ({
    status: s, items: exercises.filter((x) => x.status === s),
  }));

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <div className="flex items-center gap-3">
            <Target size={20} color="var(--cyan)" strokeWidth={1.5} />
            <div className="overline">// READINESS_EXERCISES</div>
          </div>
          <button onClick={openCalendar} className="btn-ghost inline-flex items-center gap-2 text-xs"
                  data-testid="open-calendar-sub">
            <Calendar size={14} /> Subscribe in Calendar
          </button>
        </div>
        <h1 className="font-display font-bold text-4xl uppercase mb-8" data-testid="exercises-title">Readiness Exercises</h1>

        <div className="grid lg:grid-cols-3 gap-6">
          {grouped.map((col) => (
            <div key={col.status} className="flat-card p-5" data-testid={`col-${col.status}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold uppercase tracking-widest text-lg">{col.status.replace("_", " ")}</h2>
                <span className="font-mono text-xs text-[var(--text-2)]">{col.items.length}</span>
              </div>
              <div className="space-y-3">
                {col.items.map((x) => (
                  <div key={x.id} className="border border-[var(--border)] p-4 hover:border-[var(--cyan)] transition-colors" data-testid={`exercise-${x.code}`}>
                    <div className="flex items-start justify-between">
                      <div className="font-mono text-xs text-[var(--cyan)]">{x.code}</div>
                      <span className={`tag ${STATUS_TAG[x.status]}`}>{x.difficulty}</span>
                    </div>
                    <div className="font-display font-bold uppercase mt-1">{x.title}</div>
                    <p className="text-sm text-[var(--text-2)] mt-2">{x.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="font-mono text-xs text-[var(--text-2)] uppercase tracking-widest">
                        {new Date(x.scheduled_at).toLocaleDateString()} &middot; TRK-{x.track}
                      </div>
                      {x.status === "scheduled" && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/calendar/exercise/${x.code}.ics`}
                          download
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)] hover:text-[var(--cyan)]"
                          title="Download .ics with 15-min reminder"
                          data-testid={`ex-add-cal-${x.code}`}
                        >
                          <CalendarPlus size={11} /> Add
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                {col.items.length === 0 && <div className="text-sm text-[var(--text-2)]">No exercises.</div>}
              </div>
            </div>
          ))}
        </div>
      </main>

      {calOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
             onClick={() => setCalOpen(false)} data-testid="calendar-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg flat-card p-7 relative">
            <button onClick={() => setCalOpen(false)} className="absolute top-3 right-3 text-[var(--text-2)] hover:text-white"
                    data-testid="calendar-modal-close">✕</button>
            <div className="overline">// CALENDAR_SUBSCRIPTION</div>
            <h2 className="font-display font-bold text-2xl uppercase mt-1">Sync to your calendar</h2>
            <p className="mt-2 text-sm text-[var(--text-2)]">Subscribe once — new exercises appear automatically.</p>
            {calBusy && <div className="mt-6 font-mono text-xs text-[var(--text-2)] flex gap-2 items-center"><Loader2 size={12} className="animate-spin" /> Minting URL…</div>}
            {cal && (
              <div className="mt-5 space-y-4">
                <div>
                  <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)] mb-1">Apple / Outlook (webcal)</div>
                  <div className="flex gap-2">
                    <input readOnly value={cal.webcal_url} className="input-tac flex-1 text-xs" data-testid="cal-webcal" />
                    <button onClick={() => copy(cal.webcal_url, "wc")} className="btn-ghost text-xs">
                      {copied === "wc" ? "Copied!" : <Copy size={12} />}
                    </button>
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)] mb-1">Google Calendar (HTTPS)</div>
                  <div className="flex gap-2">
                    <input readOnly value={cal.ics_url} className="input-tac flex-1 text-xs" data-testid="cal-ics" />
                    <button onClick={() => copy(cal.ics_url, "ics")} className="btn-ghost text-xs">
                      {copied === "ics" ? "Copied!" : <Copy size={12} />}
                    </button>
                  </div>
                  <a href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl" target="_blank" rel="noreferrer"
                     className="font-mono text-[10px] text-[var(--cyan)] mt-1 inline-block">
                    Open Google "Add by URL" →
                  </a>
                </div>
                <p className="font-mono text-[10px] text-[var(--text-2)] border-t border-[var(--border)] pt-3">
                  // refresh interval: ~1h · token unique to your account · revoke from your profile any time
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
